// Minimal toast system — avoids a full library dependency.
// Slides in from top-right with a caret animation, fades/slides out.

import { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { cn } from '../../lib/cn.js';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} });

let toastCount = 0;

const AUTO_DISMISS_MS = 4000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++toastCount;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, AUTO_DISMISS_MS + 200); // +200ms buffer for exit animation
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Top-right on desktop, bottom-center on mobile */}
      <div
        className="fixed bottom-20 right-4 sm:bottom-auto sm:top-4 sm:right-4 z-[200] flex flex-col gap-2 w-full max-w-[340px] px-4 sm:px-0 pointer-events-none"
        aria-live="polite"
        aria-label="Notifications"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const [leaving, setLeaving] = useState(false);

  // Auto-dismiss with exit animation
  useEffect(() => {
    const exitTimer = setTimeout(() => setLeaving(true), AUTO_DISMISS_MS);
    return () => clearTimeout(exitTimer);
  }, []);

  // After exit animation completes, remove from DOM
  useEffect(() => {
    if (!leaving) return;
    const removeTimer = setTimeout(() => onDismiss(toast.id), 200);
    return () => clearTimeout(removeTimer);
  }, [leaving, toast.id, onDismiss]);

  function handleDismiss() {
    setLeaving(true);
  }

  const icons = {
    success: <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />,
    error: <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />,
    info: <Info className="w-4 h-4 text-primary flex-shrink-0" />,
  };

  const styles = {
    success: 'border-l-4 border-l-success border-green-200 bg-green-50',
    error: 'border-l-4 border-l-danger border-red-200 bg-red-50',
    info: 'border-l-4 border-l-primary border-blue-200 bg-blue-50',
  };

  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg pointer-events-auto',
        leaving ? 'animate-toast-out' : 'animate-toast-in',
        styles[toast.type]
      )}
      role="status"
    >
      {icons[toast.type]}
      <p className="text-sm text-primary-text flex-1 leading-snug">{toast.message}</p>
      <button
        onClick={handleDismiss}
        className="text-secondary hover:text-primary-text p-0.5 min-h-[24px] min-w-[24px] flex items-center justify-center flex-shrink-0 rounded transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
